import React, { useRef, useEffect, useState } from 'react';
import Swiper from 'react-id-swiper';
import { Box, Container, Typography } from '@material-ui/core';
import cx from 'classnames';

import { BREAKPOINTS_VALUES } from '../../theme';
import { Video } from '../Video';
import { useEventListener } from '../../utils/hooks';
import { secondsToHHHmmm, secondsToHms } from '../../utils/helpers';
import useStyles from './styles';
import { Pathway } from '../Pathway';
import { DEFAULT_URL } from '../../pages/DiscoverPage/const';

type SliderBreakpoint = {
  slidesPerView?: number;
  slidesOffsetAfter?: number;
};

const getDefaultBreakpoints = (
  isPortraitOrientation: boolean,
): { [p: string]: SliderBreakpoint } => ({
  [BREAKPOINTS_VALUES.xl]: {
    slidesPerView: isPortraitOrientation ? 6 : 5.2,
  },
  [BREAKPOINTS_VALUES.lg]: {
    slidesPerView: isPortraitOrientation ? 5 : 4.2,
  },
  [BREAKPOINTS_VALUES.md]: {
    slidesPerView: isPortraitOrientation ? 4 : 3.2,
  },
  [BREAKPOINTS_VALUES.sm]: {
    slidesPerView: isPortraitOrientation ? 3 : 2.2,
    slidesOffsetAfter: isPortraitOrientation ? 0 : 16,
  },
  [BREAKPOINTS_VALUES.xs]: {
    slidesPerView: isPortraitOrientation ? 2.5 : 1.2,
    slidesOffsetAfter: isPortraitOrientation ? 0 : 16,
  },
});

interface VideosBlockProps {
  data?: any;
  getBreakpoints?: (isPortraitOrientation: boolean) => { [p: string]: SliderBreakpoint };
  handleClick?: (id: string) => void;
  withArrows?: boolean;
  classes?: {
    wrapper?: string;
    container?: string;
    heading?: string;
    videoWrapper?: string;
  };
}

const VideosBlock: React.FC<VideosBlockProps> = ({
  data,
  getBreakpoints = getDefaultBreakpoints,
  classes,
  withArrows = true,
}) => {
  const { background, orientation, heading } = data;

  const videos = data?.items;
  const isPortraitOrientation = orientation === 'Portrait';
  const [videoWidth, setVideoWidth] = useState<number | null>(null);
  const [hasMaxWidth, setHasMaxWidth] = useState(true);
  const videoWrapRef = useRef(null);

  const setVideoWidthHandler = () => {
    //add timeout, because gets wrong value after resize
    setTimeout(() => {
      // @ts-ignore
      const videoWidthValue = videoWrapRef?.current?.clientWidth;
      setVideoWidth(videoWidthValue);
    }, 500);
  };

  useEffect(() => {
    setTimeout(() => {
      setHasMaxWidth(false);
    }, 0);
    setVideoWidthHandler();
  }, []);

  useEventListener('resize', setVideoWidthHandler);

  const defaultClasses = useStyles(background, isPortraitOrientation, videoWidth, hasMaxWidth)();

  const videosOnDesktopCount = isPortraitOrientation ? 2.5 : 1.2;

  const dynamicSlidesOffsetAfter = videoWidth ? videoWidth * 0.2 + 50 : 100;
  const breakpoints = getBreakpoints(isPortraitOrientation);

  const swiperSettings = {
    slidesPerView: videosOnDesktopCount,
    spaceBetween: 16,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      invert: true,
    },
    navigation: withArrows
      ? {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }
      : {},
    direction: 'horizontal',
    rebuildOnUpdate: true,
    slidesOffsetAfter: isPortraitOrientation ? 0 : dynamicSlidesOffsetAfter,
    breakpoints,
  };

  return (
    <Box
      className={cx(
        defaultClasses.wrapper,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'videos-block',
        classes?.wrapper,
      )}
    >
      <Container
        className={cx(defaultClasses.container, 'landscape-container', classes?.container)}
        maxWidth={isPortraitOrientation ? undefined : false}
      >
        {heading && (
          <Typography
            variant="h2"
            component="h2"
            align="center"
            className={cx(defaultClasses.heading, classes?.heading)}
          >
            {heading}
          </Typography>
        )}

        <Swiper {...swiperSettings}>
          {videos &&
            videos.map((item, index) => {
              const type = item?.type;
              if (type === 'pathway') {
                const id = item?.pathway?.id;
                const name = item?.pathway?.name;
                const description = item?.pathway?.description;
                const imageUrl = item?.pathway?.image_url;
                const duration = secondsToHHHmmm(item?.pathway?.duration);
                const countVideos = item?.pathway?.count_videos;
                const slug = item?.pathway?.slug;
                const progress = item?.pathway?.progress;
                return (
                  <div
                    ref={videoWrapRef}
                    className={cx(defaultClasses.pathwayWrapper, classes?.videoWrapper)}
                    key={index}
                  >
                    <Pathway
                      videoWidth={videoWidth}
                      id={id}
                      name={name}
                      description={description}
                      imageUrl={imageUrl}
                      duration={duration}
                      countVideos={countVideos}
                      slug={slug}
                      background={background}
                      progress={progress}
                    />
                  </div>
                );
              } else {
                const id = item?.video?.id;
                const title = item?.video?.title;
                const videoUrl = item?.video?.url;
                const userName = item?.video?.user?.name;
                const difficulty = item?.video?.difficulty;
                const categories = item?.video?.categories;
                const previewUrl = item?.video?.preview_url;
                const description = item?.video?.description;
                const duration = secondsToHms(item?.video?.duration);
                const progress = item?.video?.progress;
                const handleClick = item?.video?.handleClick;
                const link = item?.video?.link;
                const verseId = item?.video?.verse_video_id;
                const expertLink = item?.video?.user?.slug
                  ? `${DEFAULT_URL}/experts/${item?.video?.user?.slug}`
                  : null;

                return (
                  <div
                    ref={videoWrapRef}
                    className={cx(defaultClasses.videoWrapper, classes?.videoWrapper)}
                    key={index}
                  >
                    <Video
                      id={id}
                      title={title}
                      src={videoUrl}
                      duration={duration}
                      userName={userName}
                      categories={categories}
                      difficulty={difficulty}
                      previewUrl={previewUrl}
                      description={description}
                      orientation={orientation}
                      videoWidth={videoWidth}
                      background={background}
                      progress={progress}
                      handleClick={handleClick}
                      durationNumb={item?.video?.duration}
                      link={link}
                      expertLink={expertLink}
                      verseId={verseId}
                    />
                  </div>
                );
              }
            })}
        </Swiper>
      </Container>
    </Box>
  );
};

export default VideosBlock;
